import { usePollAISuggestionRequest } from 'hooks/queries/articles/aiSuggestionsRequests/usePollAISuggestionRequest'
import { GenerateRequest, SuggestionRequestStatus, SuggestionRequestWithRequester } from 'types'
import { dismissToastMessages, showToast } from 'common/ToastMessage'
import { FC, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useShowFailedGenerateRequestToast } from 'screens/ProseEdit/components/ProcessingSuggestionToast/useShowFailedGenerateRequestToast'
import { EditorView } from 'prosemirror-view'
import { GeneratingPostToast } from './GeneratingPostToast'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { ErrorBoundary } from '@sentry/react'
import { altCopyOptions, totalCopyOptions } from './utils'

interface Props {
  id: number
  articleId: string
  layerId: string
  onStreaming?: (jobId: string) => any
  onRequestFetched?: (request: SuggestionRequestWithRequester) => any
  view?: EditorView
  streamContent: string
  channel: ChannelType
}

const PollSuggestionRequest: FC<React.PropsWithChildren<Props>> = observer(
  ({ id, articleId, layerId, onStreaming, onRequestFetched, view, streamContent, channel }) => {
    const showFailedGenerateRequest = useShowFailedGenerateRequestToast({ view })
    const key = `request${id}`
    const splitter = [ChannelType.InstagramPosts, ChannelType.FacebookPost].includes(channel)
      ? '## Hashtag Inspiration'
      : '### Image options'

    const copies = altCopyOptions(streamContent.split(splitter)[0], channel)

    const removeToast = () => {
      setTimeout(() => dismissToastMessages(), 5000)
    }

    useEffect(() => {
      return () => removeToast()
    }, [])

    usePollAISuggestionRequest(
      {
        articleId,
        layerId,
        id,
      },
      {
        onSuccess: async ({ request }) => {
          onRequestFetched?.(request)
          const isFailed = request.status === SuggestionRequestStatus.Failed
          const isComplete = [
            SuggestionRequestStatus.Successful,
            SuggestionRequestStatus.Failed,
            SuggestionRequestStatus.Aborted,
          ].includes(request.status)

          if (!isComplete && request.job_id) {
            onStreaming?.(request.job_id)
          }

          showToast(
            <GeneratingPostToast
              copies={copies ? copies - 1 : 0}
              generatingImages={request.prompt.include_images && streamContent.includes('### Image options')}
              total={totalCopyOptions(channel, request) - 1}
              status={request.status}
            />,
            request.status === SuggestionRequestStatus.Successful ? 5 : 0,
            key,
          )

          if (isComplete) {
            removeToast()
          }

          if (isFailed) {
            showFailedGenerateRequest(request as unknown as GenerateRequest)
          }
        },
      },
    )

    return null
  },
)

const WithErrorBoundary = (props: Props) => (
  <ErrorBoundary>
    <PollSuggestionRequest {...props} />
  </ErrorBoundary>
)

export { WithErrorBoundary as PollSuggestionRequest }
